/**
 * 筛选器基础信息配置组件
 * 包含：数据表选择、字段选择、类型选择、名称输入
 *
 * columnStatusRef 与 FilterColumn 配合维护两类 chartId（详见 FilterColumn 文件头注释）：
 * - chartId：多图逗号列表，供 getTableInfoList 拉全量字段
 * - customColumnChartId：选中自定义字段的单图 id，供 Select 回显
 */

import React, { useEffect, useMemo, useRef } from 'react';
import { Form, Radio, Input, notification } from 'tea-component';
import { useIntl } from 'umi';
import { useRequest } from 'ahooks';
import { columnCheck } from '@dbl/services/dataSource';
import FilterDataset from '@/pages/datalab/pages/WorkBench/components/RightModal/ExitModal/EditFilter/components/FilterDataset';
import FilterColumn, { IFilterColumnRef } from '@/pages/datalab/pages/WorkBench/components/RightModal/ExitModal/EditFilter/components/FilterColumn';
import FilterDefaultValue from '../FilterDefaultValue';
import type { IColumnDateCheckResult } from '@dbl/services/dataSource';
import type {
  FilterAction,
  TFilterType,
  IChartInfo,
  IExistingFilter,
  IFilterState,
} from '../../types';
import { getDatasetListChartId } from '../../utils';

interface IFilterBasicInfoProps {
  state: IFilterState;
  dispatch: React.Dispatch<FilterAction>;
  gameCode: string;
  viewList: IChartInfo[];
  existingFilters: IExistingFilter[];
  isEn: boolean;
  isDisabledCompare: boolean;
}

const FilterBasicInfo: React.FC<IFilterBasicInfoProps> = ({
  state,
  dispatch,
  gameCode,
  viewList,
  existingFilters,
  isDisabledCompare,
  isEn,
}) => {
  const { formatMessage } = useIntl();
  const filterColumnRef = useRef<IFilterColumnRef>(null);
  const columnStatusRef = useRef<any>(state.columnInfo || {});

  // 日期格式校验请求
  // 根据需求文档：当字段类型不是 date 且用户手动从 string 切换到 date 时，需要触发日期格式校验
  // loading: checkDateLoading
  const { runAsync: checkDateAsync } = useRequest(columnCheck, {
    manual: true,
  });

  // 计算 allFilterDatasetId - 用于传递给 FilterDataset 组件
  // FilterDataset 会使用此列表来排除已使用的数据表，避免重复选择
  const allFilterDatasetId = existingFilters.map(f => f.dataset);

  // 从 viewList 聚合当前数据表关联的全部图表 id，避免 FilterColumn 列表请求被收窄为单图
  const listChartId = useMemo(
    () =>
      getDatasetListChartId(viewList, state.dataset) ||
      columnStatusRef.current?.chartId ||
      '',
    [viewList, state.dataset],
  );

  useEffect(() => {
    if (!state.dataset || !listChartId) {
      return;
    }
    columnStatusRef.current = {
      ...columnStatusRef.current,
      chartId: listChartId,
    };
  }, [state.dataset, listChartId]);

  // 类型选项
  const filterTypeOptions = [
    { value: 'date', text: formatMessage({ id: 'dbl_create_dashboard_date' }) },
    { value: 'string', text: formatMessage({ id: 'dbl_create_dashboard_value' }) },
  ];

  // 同步 reducer 中的 columnInfo 到 ref，但不得覆盖 ref.chartId（列表用多 id）和已选 customColumnChartId
  if (state.columnInfo) {
    const listChartId = columnStatusRef.current?.chartId;
    columnStatusRef.current = {
      ...columnStatusRef.current,
      ...state.columnInfo,
      chartId: listChartId || '',
      customColumnChartId:
        columnStatusRef.current?.customColumnChartId ||
        (state.columnInfo.isCustomField ? state.columnInfo.chartId : '') ||
        '',
    };
  }

  // 处理类型变更（包含日期类型校验逻辑）
  const handleFilterTypeChange = async (value: TFilterType): Promise<void> => {
    // 如果切换到日期类型，且字段类型不是 date，需要校验
    if (value === 'date' && state.columnInfo?.columnType !== 'date') {
      // 设置校验中状态
      dispatch({
        type: 'SET_UI_STATE',
        payload: { dateTypeValidating: true, dateTypeDisabled: true },
      });

      try {
        // 发起日期格式校验
        const result = (await checkDateAsync({
          game_code: gameCode,
          table_id: state.dataset || '',
          column: state.column || '',
        })) as IColumnDateCheckResult;
        if (result?.date_format) {
          // 校验成功：将筛选器类型修改为 date
          dispatch({ type: 'SET_FILTER_TYPE', payload: value });
          dispatch({
            type: 'SET_UI_STATE',
            payload: { dateTypeValidating: false, dateTypeDisabled: false },
          });
        } else {
          // 校验失败：保持原类型，禁用 date 选项
          dispatch({
            type: 'SET_UI_STATE',
            payload: { dateTypeValidating: false, dateTypeDisabled: true },
          });
          // 显示错误提示
          notification.error({
            description: isEn
              ? `${state.columnInfo?.displayNameEn || state.column} unable to convert to date type`
              : `${state.columnInfo?.displayName || state.column} 无法转换成日期`,
            duration: 3000,
          });
        }
      } catch (error) {
        // 校验异常：保持原类型，禁用 date 选项
        dispatch({
          type: 'SET_UI_STATE',
          payload: { dateTypeValidating: false, dateTypeDisabled: true },
        });
        notification.error({
          description: isEn
            ? `${state.columnInfo?.displayNameEn || state.column} unable to convert to date type`
            : `${state.columnInfo?.displayName || state.column} 无法转换成日期`,
          duration: 3000,
        });
      }
    } else {
      // 直接切换类型（字段类型是 date 或切换到 string）
      dispatch({ type: 'SET_FILTER_TYPE', payload: value });
    }
  };

  return (
    <Form>
      <FilterDataset
        status={state.validation.datasetError ? 'error' : undefined}
        dataset={state.dataset || ''}
        onChange={(value: string, context?: any) => {
          // 切换数据表：重置选中字段归属，并写入新的多图 listChartId
          const nextListChartId =
            context?.option?.chartId ||
            getDatasetListChartId(viewList, value) ||
            '';
          columnStatusRef.current = {
            ...columnStatusRef.current,
            chartId: nextListChartId,
            customColumnChartId: '',
          };
          dispatch({ type: 'SET_DATASET', payload: value || null });
        }}
        gameCode={gameCode}
        filterType={state.filterType}
        viewList={viewList}
        allFilterDatasetId={allFilterDatasetId}
      />

      <FilterColumn
        ref={filterColumnRef}
        status={
          state.validation.columnError === 'deleted'
            ? 'field_error'
            : state.validation.columnError === 'invalid'
              ? 'reference_field_error'
              : state.validation.columnError
                ? 'error'
                : undefined
        }
        dataset={state.dataset || ''}
        column={state.column || ''}
        // chartId：选中自定义字段的单图 id（state.columnInfo）；listChartId：拉字段列表用的多图 id
        chartId={state.columnInfo?.chartId}
        listChartId={listChartId}
        columnStatusRef={columnStatusRef}
        changeStatus={(key: string, status: string) => {
          dispatch({
            type: 'SET_VALIDATION',
            payload: { [key]: status },
          });
        }}
        onChange={(value: string, context: any) => {
          // 选中字段时记录 customColumnChartId；columnInfo.chartId 仅存单图 id，与 ref.chartId（多图列表）分离
          const optionChartId =
            context?.option?.chartId !== undefined &&
            context?.option?.chartId !== null
              ? String(context.option.chartId)
              : '';
          columnStatusRef.current = {
            ...columnStatusRef.current,
            customColumnChartId: optionChartId,
          };
          const columnInfo = {
            columnType: context?.option?.type || 'string',
            columnDatasetId: context?.option?.tableId || state.dataset || '',
            isNumeric: ['number', 'float', 'percent'].includes(
              context?.option?.type || ''
            ),
            isCustomField: !!optionChartId,
            chartId: optionChartId || undefined,
            sqlExpression: context?.option?.sql_expression,
            displayName: context?.option?.display_name,
            displayNameEn: context?.option?.display_name_en,
            name: value,
          };
          dispatch({
            type: 'SET_COLUMN',
            payload: { column: value, columnInfo },
          });
        }}
        gameCode={gameCode}
      />

      <Form.Item
        label={formatMessage({ id: 'dbl_create_dashboard_filter_type' })}
        showStatusIcon={false}
        status={state.validation.typeError ? 'error' : undefined}
        message={
          state.validation.typeError === 'error'
            ? formatMessage(
              { id: 'dbl_common_please_select' },
              { name: formatMessage({ id: 'dbl_create_dashboard_filter_type' }) }
            )
            : state.validation.typeError || ''
        }
      >
        <Radio.Group
          value={state.filterType || undefined}
          onChange={(value: string) => {
            handleFilterTypeChange(value as TFilterType);
          }}
          disabled={!state.column || state.ui.dateTypeValidating}
        >
          {filterTypeOptions.map(opt => (
            <Radio
              key={opt.value}
              name={opt.value}
              disabled={state.ui.dateTypeDisabled && opt.value === 'date'}
            >
              {opt.text}
            </Radio>
          ))}
        </Radio.Group>
      </Form.Item>

      <Form.Item
        label={formatMessage({ id: 'dbl_create_dashboard_filter_name' })}
        showStatusIcon={false}
        status={state.validation.nameError ? 'error' : undefined}
        message={
          state.validation.nameError === 'error'
            ? formatMessage({ id: 'dbl_common_cannot_be_empty' })
            : state.validation.nameError || ''
        }
      >
        <Input
          placeholder={formatMessage({ id: 'dbl_create_dashboard_filter_name_placeholder' })}
          value={state.filterName}
          onChange={(value: string) => {
            dispatch({ type: 'SET_FILTER_NAME', payload: value });
          }}
          disabled={!state.column}
        />
      </Form.Item>

      {/* 默认值配置区域 */}
      {/* 根据需求文档：字段选择后，显示默认值配置区域 */}
      {state.column && state.filterType && (
        <FilterDefaultValue
          state={state}
          dispatch={dispatch}
          gameCode={gameCode}
          isDisabledCompare={isDisabledCompare}
          isEn={isEn}
          filterColumnRef={filterColumnRef}
        />
      )}
    </Form>
  );
};

export default FilterBasicInfo;
